import React from 'react';
import { useTranslation } from 'react-i18next';

import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import { ExportDropdown } from '@/components/ui/filters';
import { Selector } from '@/components/ui/inputs/selector';
import { DynamicFiltersCard } from '@/components/ui/cards/DynamicFiltersCard';
import type { FilterField } from '@/components/ui/cards/DynamicFiltersCard';
import { normalizeSelectValue } from '@/utils';
import type { UIExportFormat } from '@/types/fileExport.types';
import type { AuditPendingFilters, UseAuditListResult } from '@/hooks/audit/useAuditList';


interface AuditFiltersCardProps {
  pendingFilters: AuditPendingFilters;
  setPendingFilters: UseAuditListResult['setPendingFilters'];
  entitiesOptions: UseAuditListResult['entitiesOptions'];
  programsOptions: UseAuditListResult['programsOptions'];
  actionsOptions: UseAuditListResult['actionsOptions'];
  usersOptions: UseAuditListResult['usersOptions'];
  combosLoaded: boolean;
  isSearching: boolean;
  canSearch: boolean;
  handleSearch: () => void;
  isExporting: boolean;
  onExport: (format: UIExportFormat) => void;
}

export const AuditFiltersCard: React.FC<AuditFiltersCardProps> = ({
  pendingFilters,
  setPendingFilters,
  entitiesOptions,
  programsOptions,
  actionsOptions,
  usersOptions,
  combosLoaded,
  isSearching,
  canSearch,
  handleSearch,
  isExporting,
  onExport,
}) => {
  const { t: translate } = useTranslation();

  const allOption = { value: '', label: translate('audit:filters.all') };

  const filterFields: FilterField[] = [
    {
      id: 'dateRange',
      type: 'dateRange',
      colSpan: 1,
      component: (
        <DateRangeFilter
          value={{
            from: pendingFilters.startDate ?? undefined,
            to: pendingFilters.endDate ?? undefined,
          }}
          onChange={(range) => {
            setPendingFilters((previous) => ({
              ...previous,
              startDate: range.from ?? null,
              endDate: range.to ?? null,
            }));
          }}
          closeOnComplete
          placeholder={translate('audit:filters.dateRange')}
        />
      ),
    },
    {
      id: 'entity',
      type: 'selector',
      colSpan: 1,
      loading: !combosLoaded,
      component: (
        <Selector
          label={translate('audit:filters.entity')}
          value={pendingFilters.entityId ?? ''}
          options={[allOption, ...entitiesOptions]}
          onChange={(value) => {
            setPendingFilters((previous) => ({
              ...previous,
              entityId: normalizeSelectValue(value) || null,
            }));
          }}
          disabled={!combosLoaded}
        />
      ),
    },
    {
      id: 'program',
      type: 'selector',
      colSpan: 1,
      loading: !combosLoaded,
      component: (
        <Selector
          label={translate('audit:filters.program')}
          value={pendingFilters.programId ?? ''}
          options={[allOption, ...programsOptions]}
          onChange={(value) => {
            setPendingFilters((previous) => ({
              ...previous,
              programId: normalizeSelectValue(value) || null,
            }));
          }}
          disabled={!combosLoaded}
        />
      ),
    },
    {
      id: 'action',
      type: 'selector',
      colSpan: 1,
      loading: !combosLoaded,
      component: (
        <Selector
          label={translate('audit:filters.action')}
          value={pendingFilters.actionId ?? ''}
          options={[allOption, ...actionsOptions]}
          onChange={(value) => {
            setPendingFilters((previous) => ({
              ...previous,
              actionId: normalizeSelectValue(value) || null,
            }));
          }}
          disabled={!combosLoaded}
        />
      ),
    },
    {
      id: 'user',
      type: 'selector',
      colSpan: 1,
      loading: !combosLoaded,
      component: (
        <Selector
          label={translate('audit:filters.user')}
          value={pendingFilters.userId ?? ''}
          options={[allOption, ...usersOptions]}
          onChange={(value) => {
            setPendingFilters((previous) => ({
              ...previous,
              userId: normalizeSelectValue(value) || null,
            }));
          }}
          disabled={!combosLoaded}
        />
      ),
    },
    {
      id: 'searchButton',
      type: 'custom',
      colSpan: 1,
      component: (
        <div className="flex items-center h-full">
          <PrimaryButton
            onClick={handleSearch}
            disabled={!canSearch}
            isLoading={isSearching}
            iconLeft={<IconScout name="search" />}
            data-testid="primary-button"
          >
            {translate('audit:actions.search')}
          </PrimaryButton>
        </div>
      ),
    },
    {
      id: 'searchSpacer',
      type: 'custom',
      colSpan: 1,
      component: <div />,
    },
    {
      id: 'exportButton',
      type: 'custom',
      colSpan: 1,
      component: (
        <div className="flex items-center justify-end h-full">
          <ExportDropdown
            onExport={onExport}
            placement="right"
            isLoading={isExporting}
            availableFormats={['EXCEL']}
          />
        </div>
      ),
    },
  ];

  return <DynamicFiltersCard fields={filterFields} />;
};

export default AuditFiltersCard;
